import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { AlertCircle, CheckCircle, Shield, Activity, RefreshCw, Loader } from 'lucide-react';
import bitdefenderService from '../../services/bitdefenderService';
import BitDefenderEndpointDialog from './BitDefenderEndpointDialog';
import { toast } from 'sonner';

export default function EndpointSecurityCard({ endpointId, endpointName }) {
  const [endpoint, setEndpoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadEndpointStatus();
    const interval = setInterval(loadEndpointStatus, 30000);
    return () => clearInterval(interval);
  }, [endpointId]);

  const loadEndpointStatus = async () => {
    setLoading(true);
    try {
      const data = await bitdefenderService.getEndpointStatus(endpointId);
      if (data && data.endpoint) {
        setEndpoint(data.endpoint);
      }
    } catch (error) {
      console.error('Error loading endpoint:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickScan = async () => {
    setScanning(true);
    try {
      const result = await bitdefenderService.triggerScan(endpointId, 1);
      if (result.success) {
        toast.success('Quick scan initiated successfully');
        await loadEndpointStatus();
      }
    } catch (error) {
      toast.error('Failed to initiate scan');
    } finally {
      setScanning(false);
    }
  };

  const handleFullScan = async () => {
    setScanning(true);
    try {
      const result = await bitdefenderService.triggerScan(endpointId, 2);
      if (result.success) {
        toast.success('Full scan initiated successfully');
        await loadEndpointStatus();
      }
    } catch (error) {
      toast.error('Failed to initiate scan');
    } finally {
      setScanning(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'protected':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'at_risk':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'infected':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Shield className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'protected':
        return 'bg-green-100 text-green-800';
      case 'at_risk':
        return 'bg-yellow-100 text-yellow-800';
      case 'infected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Loader className="w-5 h-5 animate-spin" />
            <CardTitle>{endpointName}</CardTitle>
          </div>
        </CardHeader>
      </Card>
    );
  }

  return (
    <>
      <Card 
        className="cursor-pointer hover:shadow-lg transition-shadow"
        onClick={() => setDialogOpen(true)}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {endpoint && getStatusIcon(endpoint.security_status)}
              <div>
                <CardTitle>{endpointName}</CardTitle>
                <CardDescription>{endpoint?.fqdn || 'Unknown FQDN'}</CardDescription>
              </div>
            </div>
            <Badge className={getStatusColor(endpoint?.security_status || 'unknown')}>
              {(endpoint?.security_status || 'unknown').toUpperCase()}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Antimalware Status</p>
              <Badge variant={endpoint?.antimalware_enabled ? 'default' : 'destructive'}>
                {endpoint?.antimalware_enabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>

            <div>
              <p className="text-sm text-gray-600">Product Status</p>
              <Badge variant={endpoint?.product_outdated ? 'destructive' : 'default'}>
                {endpoint?.product_outdated ? 'Outdated' : 'Current'}
              </Badge>
            </div>

            <div>
              <p className="text-sm text-gray-600">License Status</p>
              <Badge variant={endpoint?.license_valid ? 'default' : 'destructive'}>
                {endpoint?.license_valid ? 'Valid' : 'Expired'}
              </Badge>
            </div>

            <div>
              <p className="text-sm text-gray-600">Threats Detected</p>
              <p className="font-semibold text-lg">{endpoint?.threat_count || 0}</p>
            </div>
          </div>

          {endpoint?.last_scan_date && (
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600">Last Scan</p>
              <p className="text-sm">
                {new Date(endpoint.last_scan_date).toLocaleString()}
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-4" onClick={(e) => e.stopPropagation()}>
            <Button 
              onClick={handleQuickScan} 
              disabled={scanning}
              variant="outline"
              size="sm"
            >
              {scanning ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Scanning...
                </>
              ) : (
                'Quick Scan'
              )}
            </Button>
            
            <Button 
              onClick={handleFullScan} 
              disabled={scanning}
              variant="outline"
              size="sm"
            >
              Full Scan
            </Button>

            <Button 
              onClick={loadEndpointStatus}
              disabled={loading || scanning}
              variant="ghost"
              size="sm"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      <BitDefenderEndpointDialog 
        endpointId={endpointId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
}
